import * as React from 'react';
import { RouteObject } from 'react-router';
import * as navigate from 'app/area/shared/navigation';
import { STATE } from 'app/constants';
import { PromptProps } from 'app/components';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { ImportMappingsListState } from './types-store';
import { codeFieldIncludeInactive, importMappingsTranslation } from './constants';
import { actionCreators } from './actions';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { deepEquals } from 'app/infrastructure/js';
import { SerializePayload } from 'app/area/search-filters/types';
import { serialize } from 'app/area/shared/codetable/serialize/serialize';
import { codeSearchArea, LoadCodeItems } from 'app/area/import-mappings/types';
import { codeDefaultFilters, codeFilters, codePredefinedFilters } from 'app/area/import-mappings/search-filters/code-filter-configuration';
import { ComplexValue } from 'app/types-shared';
import { ImportMappingsToolbar } from 'app/area/import-mappings/list-items/components/import-mappings-toolbar';
import { getCodetableMetadata } from 'app/area/import-mappings/selectors';
import { CodetableListComponent } from 'app/area/import-mappings/list-items/codetable-list-component';
import { ImportMappingsPrompt } from 'app/area/import-mappings/top-filters-prompt';
import { withRouter } from 'app/components/enhancers/with-router';
import { getNumberOfModifiedImportCodes } from 'app/area/import-mappings/list-items/modify-import-code';
import { filterId } from 'app/area/search-filters/constants';

export type ImportMappingsProps = DefaultContainerProps
  & PromptProps
  & ImportMappingsListState
  & typeof actionCreators
  & { route: RouteObject };

class ImportMappingsPure extends React.PureComponent<ImportMappingsProps> {
  public static statePath: string = STATE.importMappings;

  public componentWillUnmount() {
    this.props.clearAll();
  }

  public componentDidMount(): void {
    this.init();
  }

  private init = () => {
    const importSetId = parseInt(this.props.params.importset, 10);
    const codetableId = parseInt(this.props.params.codetable, 10);

    if (isDefinedIncludingZero(importSetId)) {
      this.props.loadImportSetData(importSetId);

      if (isDefinedIncludingZero(codetableId)) {
        this.props.loadCodetableData(codetableId);
        !this.props.codetableMetadata && this.props.loadCodetableMetadata(codetableId);
      }
      this.loadCodeItems({
        importSet: { value: importSetId },
        ...(codetableId && { codetable: { value: codetableId } })
      });
    }
  };

  private clearUrl = () => navigate.toImportMappingsList();

  private updateImportSet = (importSet: ComplexValue<number>) => {
    this.clearUrl();
    this.props.clearList();
    this.props.clearCurrentCode();

    this.props.updateImportSet(importSet);
    isDefinedIncludingZero(importSet?.value)
      && isDefinedIncludingZero(this.props.codetable?.value)
      && this.loadCodeItems({
        importSet,
        codetable: this.props.codetable,
        ...(isDefinedIncludingZero(this.props.category?.value) && { category: this.props.category })
      });
  };

  private updateCodetable = (codetable: ComplexValue<number>) => {
    this.clearUrl();
    this.props.clearCurrentCode();

    !codetable && this.props.clearList();
    !!this.props.category && this.props.clearMetadata('codetableCategory');

    this.props.updateCodetable(codetable);
    isDefinedIncludingZero(codetable?.value) && this.props.loadCodetableMetadata(codetable?.value);
    isDefinedIncludingZero(this.props.category?.value) && this.props.updateCategory(undefined);

    !Object.keys(this.props.requestedSearchModel)
      .some(filterParam => filterParam !== filterId.includeInactiveCodes)
      && isDefinedIncludingZero(this.props.importSet?.value)
      && isDefinedIncludingZero(codetable?.value)
      && this.loadCodeItems({
        importSet: this.props.importSet,
        codetable,
        category: undefined
      });
  };

  private updateCategory = (category: ComplexValue<number>) => {
    this.props.clearCurrentCode();
    this.props.updateCategory(category);

    this.loadCodeItems({
      importSet: this.props.importSet,
      codetable: this.props.codetable,
      category
    });
  };

  private getCurrentIncludeInactive = () => codeFieldIncludeInactive[this.props.includeInactive];

  private loadCodeItems = (loadCodeItemsPayload: LoadCodeItems = {}) => {
    const {
      importSet: { value: importSetId } = {},
      codetable: { value: codetableId } = {},
      category: { value: categoryId } = {},
      parent, payload } = loadCodeItemsPayload;
    const { importSet, codetable, category, requestedSearchModel, updateSearchModel } = this.props;
    const newSearchModel = payload ? serialize(payload) : undefined;
    const didSearchModelChanged = newSearchModel && !deepEquals(newSearchModel, requestedSearchModel);
    const importSetValue = didSearchModelChanged ? importSet?.value : importSetId;
    const codetableValue = didSearchModelChanged ? codetable?.value : codetableId;
    const categoryValue = didSearchModelChanged ? category?.value : categoryId;

    if (isDefinedIncludingZero(importSetValue) && isDefinedIncludingZero(codetableValue)) {
      this.props.loadCodeItems({
        importSetId: importSetValue,
        codetableId: codetableValue,
        ...(isDefinedIncludingZero(categoryValue) && { categoryId: categoryValue }),
        parentId: isDefinedIncludingZero(parent?.id) ? parent.id : undefined,
        startIndex: isDefinedIncludingZero(parent?.startIndex) ? parent.startIndex : undefined,
        maxResults: undefined,
        searchModel: didSearchModelChanged && newSearchModel ? newSearchModel : requestedSearchModel,
        includeInactiveCodes: this.getCurrentIncludeInactive()
      });
    }

    didSearchModelChanged && updateSearchModel(newSearchModel);
  };

  private updateSearchModel = (payload: SerializePayload) => {
    const newSearchModel = payload ? serialize(payload) : undefined;
    !deepEquals(newSearchModel, this.props?.requestedSearchModel) && this.loadCodeItems({ payload });
  };

  private loadFromParent = (parentId: number, startIndex: number) => this.loadCodeItems({
    parent: { id: parentId, startIndex },
    importSet: this.props.importSet,
    codetable: this.props.codetable,
    category: this.props.category
  });

  private isFetching = () => {
    const { codeListBranchState, fetchingItemAccess } = this.props;
    return !!fetchingItemAccess || (codeListBranchState && codeListBranchState?.[0]?.fetching);
  };

  private rootBranch = () => this.props?.codeListBranch?.find(branch => branch.id === undefined);

  private shouldShowFiltersDrawer = () => !!this.props.importSet && !!this.props.codetable;

  private isHierarchy = () => {
    const codetableMetadata = getCodetableMetadata(this.props.codetableMetadata, this.props.codetable?.value);
    return !!codetableMetadata?.hierarchy;
  };

  private emptyRequestSearchModelFilters = () => {
    return !this.props.requestedSearchModel?.query?.text
      && !this.props.requestedSearchModel?.code?.text
      && !this.props.requestedSearchModel?.importSet?.valueOperator;
  };

  private isHierarchyMode = () => this.isHierarchy() && this.emptyRequestSearchModelFilters();

  private isCodetableWithCategory = () => {
    const codetableMetadata = getCodetableMetadata(this.props.codetableMetadata, this.props.codetable?.value);
    return isDefinedIncludingZero(codetableMetadata?.codetableCategory?.id);
  };

  public render() {

    const {
      route, category, codeListBranch, translation, codetableMetadata, modifiedItems, codetable, importSet,
      loadCategoryOptions, options
    } = this.props;

    const rootBranch = this.rootBranch();
    const importSetId = this.props.importSet?.value;
    const codetableId = this.props.codetable?.value;
    const shouldRenderList = !!codeListBranch && codeListBranch.length > 0 && rootBranch
      && rootBranch.numberOfResults > 0;
    const predefinedFilters = codePredefinedFilters(translation);
    const customFilters = codeFilters(importSet?.value, translation);
    const defaultFilterValues = codeDefaultFilters(translation);
    const hideDrawer = !this.shouldShowFiltersDrawer();
    const shouldPrompt = getNumberOfModifiedImportCodes(modifiedItems) > 0;

    const topToolbar = <ImportMappingsToolbar
      metadata={codetableMetadata}
      shouldPrompt={shouldPrompt}
      codetable={codetable}
      importSet={importSet}
      category={category}
      options={options}
      loadCodetableOptions={loadCategoryOptions}
      updateCodetable={this.updateCodetable}
      updateCategory={this.updateCategory}
      updateImportSet={this.updateImportSet}
      translation={translation}
    />;

    const ImportMappingsPromptWithRouter = withRouter(ImportMappingsPrompt);

    return <>
      <ImportMappingsPromptWithRouter
        route={route}
        shouldPrompt={shouldPrompt}
        translation={translation} />

      <CodetableListComponent
        {...this.props}
        isFetching={this.isFetching()}
        isHierarchy={this.isHierarchy()}
        isHierarchyMode={this.isHierarchyMode()}
        isCodetableWithCategory={this.isCodetableWithCategory()}
        shouldRenderSpinLoader={this.isFetching()}
        displayNoContent={this.emptyRequestSearchModelFilters() && !shouldRenderList
          && !this.isFetching() && !this.props.codetable}
        loadFromParent={this.loadFromParent}
        hideDrawer={hideDrawer}
        hideSearch={true}
        wideScreen={true}
        topToolbar={topToolbar}
        filtersConfig={{
          area: codeSearchArea,
          ...(isDefinedIncludingZero(importSetId)
            && isDefinedIncludingZero(codetableId)
            && { customConfig: { codetableId, importSetIt: importSetId } })
        }}
        predefinedFilters={predefinedFilters}
        defaultFilterValues={defaultFilterValues}
        queryFilters={undefined}
        customFilters={customFilters}
        updateSearchModel={this.updateSearchModel}
        category={category}
        metadata={codetableMetadata}
        updateCodetable={this.updateCodetable}
        updateCategory={this.updateCategory}
        updateImportSet={this.updateImportSet}
        numberOfResults={rootBranch?.numberOfResults}
      />
    </>;
  }
}

const container = containerFactory(ImportMappingsPure, actionCreators, {}, importMappingsTranslation);

export { container as ImportMappings };
